import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  axiosInstance, fmt, getApiErrorMessage, inputCls,
  Field, Modal, ROLE_META, RoleBadge, StatusBadge,
} from './_shared';

export default function RoleApplicationsTab() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [reviewing, setReviewing] = useState(null);
  const [reviewForm, setReviewForm] = useState({ status: 'approved', admin_notes: '', overrides: {} });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType) params.role_type = filterType;
      if (filterStatus) params.status = filterStatus;
      const r = await axiosInstance.get('/admin/role-applications', { params });
      setApps(r.data);
    } catch { toast.error('Failed to load applications'); }
    finally { setLoading(false); }
  }, [filterType, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const openReview = app => {
    setReviewing(app);
    setReviewForm({ status: app.status === 'pending' ? 'approved' : app.status, admin_notes: app.admin_notes || '', overrides: {} });
  };

  const submit = async () => {
    try {
      await axiosInstance.patch(`/admin/role-applications/${reviewing.role_type}/${reviewing.id}`, reviewForm);
      toast.success('Application updated');
      setReviewing(null);
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  const profileFields = {
    player:     ['sport', 'experience_level', 'city', 'phone', 'age', 'bio'],
    creator:    ['content_type', 'bio', 'city', 'phone', 'instagram', 'youtube'],
    team_owner: ['team_name', 'sport', 'city', 'phone', 'player_count', 'description'],
    sponsor:    ['company_name', 'industry', 'budget_range', 'phone', 'website', 'description'],
  };

  return (
    <div className="space-y-4">
      {reviewing && (
        <Modal title={`Review — ${ROLE_META[reviewing.role_type]?.label || reviewing.role_type} Application`} onClose={() => setReviewing(null)} wide>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-bold text-gray-400 mb-3 uppercase tracking-wide">Submitted Details</p>
              <div className="grid grid-cols-2 gap-3">
                {(profileFields[reviewing.role_type] || []).map(f => (
                  <div key={f}>
                    <p className="text-xs text-gray-400 capitalize">{f.replace(/_/g, ' ')}</p>
                    <input className={`${inputCls} mt-0.5`}
                      defaultValue={reviewing[f] || ''}
                      onChange={e => setReviewForm(rv => ({ ...rv, overrides: { ...rv.overrides, [f]: e.target.value } }))}
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">Applied by: <span className="font-bold text-gray-600">{reviewing.user_name} ({reviewing.user_email})</span></p>
              <p className="text-xs text-gray-400">Applied on: {fmt(reviewing.applied_at)}</p>
            </div>
            <Field label="Decision">
              <select className={inputCls} value={reviewForm.status} onChange={e => setReviewForm(f => ({ ...f, status: e.target.value }))}>
                <option value="approved">Approve</option>
                <option value="rejected">Reject</option>
                <option value="pending">Keep Pending</option>
              </select>
            </Field>
            <Field label="Admin Notes">
              <textarea className={inputCls} rows={3} value={reviewForm.admin_notes} onChange={e => setReviewForm(f => ({ ...f, admin_notes: e.target.value }))} placeholder="Optional notes for the applicant…" />
            </Field>
            <div className="flex gap-3">
              <button onClick={() => setReviewing(null)} className="flex-1 py-3 rounded-2xl border-2 border-gray-200 font-bold text-sm">Cancel</button>
              <button onClick={submit}
                className={`flex-1 py-3 rounded-2xl font-extrabold text-white text-sm ${reviewForm.status === 'approved' ? 'bg-green-500' : reviewForm.status === 'rejected' ? 'bg-red-500' : 'bg-[#2B3A67]'}`}>
                {reviewForm.status === 'approved' ? 'Approve' : reviewForm.status === 'rejected' ? 'Reject' : 'Update'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      <div className="flex gap-2 flex-wrap">
        <select className={`${inputCls} flex-1`} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Role Types</option>
          <option value="player">Player</option>
          <option value="creator">Creator</option>
          <option value="team_owner">Team Owner</option>
          <option value="sponsor">Sponsor</option>
        </select>
        <select className={`${inputCls} flex-1`} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E54562]" /></div>
      ) : apps.length === 0 ? (
        <div className="text-center py-16 text-gray-400"><i className="ph-duotone ph-clipboard-text text-5xl mb-3" /><p>No applications</p></div>
      ) : (
        <div className="space-y-3">
          {apps.map(app => (
            <div key={app.id} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <RoleBadge role={app.role_type} />
                    <StatusBadge status={app.status} />
                  </div>
                  <p className="font-extrabold mt-1">{app.user_name}</p>
                  <p className="text-xs text-gray-400">{app.user_email}</p>
                </div>
                <button onClick={() => openReview(app)}
                  className="px-3 py-2 bg-[#2B3A67] text-white text-xs font-bold rounded-xl shrink-0">
                  Review
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                {(profileFields[app.role_type] || []).slice(0, 4).map(f => app[f] ? (
                  <div key={f}><span className="text-gray-400 capitalize">{f.replace(/_/g, ' ')}: </span><span className="font-bold">{app[f]}</span></div>
                ) : null)}
              </div>
              <p className="text-xs text-gray-300 mt-2">Applied {fmt(app.applied_at)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Google Maps Location Picker ──────────────────────────────────────────────
const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const DEFAULT_CENTER = { lat: 19.076, lng: 72.877 }; // Mumbai

// Note: same constants are also exported from ./MapLocationPicker — kept local
// here to avoid coupling RoleApplicationsTab to MapLocationPicker's module
// graph (they only share two trivial constants).
